import { isCalendarBlockIdleTime } from "@caredna-marlin/utils/calendarBlockHelper";
import { calculateProviderUtilization } from "@caredna-marlin/utils/kpisHelper";
import { ShiftStatusEnum, VisitEventStatusEnum } from "~/graphql/__generated__/graphql";

import { LocalCalendarBlock, LocalShift, LocalVisitResource } from "~/hooks/visitDragDropReducer";
import { visitResourceStatus } from "~/utils/statuses";

export const matchVisitResourceToShift = (providerId: number | null, visitResource: LocalVisitResource, localShifts: LocalShift[]) => {
  if (!providerId) { return undefined }

  const visitStartTime = new Date(visitResource.startTime).getTime();
  const visitEndTime = new Date(visitResource.endTime).getTime();
  return localShifts.find((shift) => {
    const shiftStartTime = new Date(shift.startTime).getTime();
    const shiftEndTime = new Date(shift.endTime).getTime();

    return (
      shift.provider?.id === providerId &&
      shift.status === ShiftStatusEnum.Confirmed &&
      visitStartTime >= shiftStartTime &&
      visitEndTime <= shiftEndTime
    );
  });
}

export const matchCalendarBlockToShift = (cb: LocalCalendarBlock, localShifts: LocalShift[]) => {
  let bestShift: LocalShift | undefined;
  let largestOverlapMs = 0;

  const blockStart = new Date(cb.startTime).getTime();
  const blockEnd = new Date(cb.endTime).getTime();

  for (const shift of localShifts) {
    if (shift.provider?.id !== cb.providerId || shift.status !== ShiftStatusEnum.Confirmed) continue;

    const shiftStart = new Date(shift.startTime).getTime();
    const shiftEnd = new Date(shift.endTime).getTime();

    const overlapMs = Math.min(blockEnd, shiftEnd) - Math.max(blockStart, shiftStart);
    if (overlapMs > largestOverlapMs) {
      largestOverlapMs = overlapMs;
      bestShift = shift;
    }
  }
  return bestShift; // undefined if no overlap
}

export const getDurationInMinutes = (startTime: Date, endTime: Date) => {
  return (endTime.getTime() - startTime.getTime()) / 60000; // Convert to minutes
}

export const getShiftCalendarBlockOverlapMinutes = (
  localShift: LocalShift,
  calendarBlocks: LocalCalendarBlock[]
) => {
  let totalOverlapMs = 0;

  const shiftStart = new Date(localShift.startTime).getTime();
  const shiftEnd = new Date(localShift.endTime).getTime();

  for (const cb of calendarBlocks) {
    if (cb.providerId !== localShift.provider?.id) continue;

    // skip canceled or blocks that are not breaks
    if (cb.eventStatus === "canceled" || !isCalendarBlockIdleTime(cb.blockType)) continue;

    const blockStart = new Date(cb.startTime).getTime();
    const blockEnd = new Date(cb.endTime).getTime();

    const overlapMs = Math.min(shiftEnd, blockEnd) - Math.max(shiftStart, blockStart);

    if (overlapMs > 0) {
      totalOverlapMs += overlapMs;
    }
  }

  return Math.floor(totalOverlapMs / 60000);
};

export const getShiftVisitResourcesOverlapMinutes = (
  localShift: LocalShift,
  localVisitResources: LocalVisitResource[]
) => {
  let totalOverlapMs = 0;

  const shiftStart = new Date(localShift.startTime).getTime();
  const shiftEnd = new Date(localShift.endTime).getTime();

  for (const vr of localVisitResources) {
    if (vr.providerId !== localShift.provider?.id) continue;

    // skip canceled visits
    if (visitResourceStatus(vr) === VisitEventStatusEnum.Canceled) continue;

    const visitStartTime = new Date(vr.startTime).getTime();
    const visitEndTime = new Date(vr.endTime).getTime();

    const overlapMs = Math.min(shiftEnd, visitEndTime) - Math.max(shiftStart, visitStartTime);

    if (overlapMs > 0) {
      totalOverlapMs += overlapMs;
    }
  }

  return Math.floor(totalOverlapMs / 60000);
};

export const getShiftUtilization = (localShift: LocalShift, visitResources: LocalVisitResource[], calendarBlocks: LocalCalendarBlock[]): number => {
  const shiftStartTime = new Date(localShift.startTime);
  const shiftEndTime = new Date(localShift.endTime);
  const totalShiftDurationInMinutes = getDurationInMinutes(shiftStartTime, shiftEndTime);

  const totalCalendarBlocksDuration = getShiftCalendarBlockOverlapMinutes(localShift, calendarBlocks);

  const totalVisitResourcesDuration = visitResources.reduce((acc, resource) => {
    // skip canceled
    if (visitResourceStatus(resource) === VisitEventStatusEnum.Canceled) return acc;

    const shift = matchVisitResourceToShift(resource.providerId, resource, [localShift]);
    if (!shift) return acc;

    const visitStartTime = new Date(resource.startTime);
    const visitEndTime = new Date(resource.endTime);
    const visitDuration = getDurationInMinutes(visitStartTime, visitEndTime);
    return acc + visitDuration;
  }, 0) ?? 0;

  return calculateProviderUtilization({
    shiftDuration: totalShiftDurationInMinutes,
    visitResourcesDuration: totalVisitResourcesDuration,
    breaksDuration: totalCalendarBlocksDuration,
  });
} 

// Maps provider id with utilization percentage
const useUtilizationPerShift = (localVisitResources: LocalVisitResource[], localShifts: LocalShift[], localCalendarBlocks: LocalCalendarBlock[]) => {
  const utilizationPerShift: Record<string, number> = {};
  localShifts.forEach((shift) => {
    if (shift.status === ShiftStatusEnum.Canceled) {
      utilizationPerShift[shift.id] = 0;
      return;
    }
    utilizationPerShift[shift.id] = getShiftUtilization(shift, localVisitResources, localCalendarBlocks);
  });

  return utilizationPerShift;
}

export default useUtilizationPerShift;
